import { Formik, Form, Field as FormikField, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Select, { components } from "react-select";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaIdCard,
    FaWhatsapp,
    FaPassport,
    FaGlobeAmericas,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBuilding,
    FaFlag,
    FaCheckCircle,
    FaArrowRight,
    FaShieldAlt,
    FaLandmark,
    FaInfoCircle,
    FaSignInAlt,
    FaCreditCard,
} from "react-icons/fa";
import styles from "./RegistrationPage.module.css";
import { newRegister } from "../../../service/register.service";

/* =========================================================
   BADGE ICONS — real SVG marks instead of emoji flags
   ========================================================= */

const IndiaFlagIcon = () => (
    <svg width="46" height="46" viewBox="0 0 46 46" role="img" aria-label="India">
        <circle cx="23" cy="23" r="23" fill="#ffffff" />
        <path d="M0 23a23 23 0 0 1 46 0z" fill="#FF9933" transform="translate(0 -7.7)" />
        <rect x="0" y="15.3" width="46" height="15.4" fill="#ffffff" />
        <path d="M0 23a23 23 0 0 0 46 0z" fill="#138808" transform="translate(0 7.7)" />
        <circle cx="23" cy="23" r="5.2" fill="none" stroke="#0B3D91" strokeWidth="0.7" />
        <circle cx="23" cy="23" r="0.9" fill="#0B3D91" />
        {Array.from({ length: 24 }).map((_, i) => (
            <line
                key={i}
                x1="23"
                y1="23"
                x2={23 + 5.2 * Math.cos((i * 15 * Math.PI) / 180)}
                y2={23 + 5.2 * Math.sin((i * 15 * Math.PI) / 180)}
                stroke="#0B3D91"
                strokeWidth="0.45"
            />
        ))}
    </svg>
);

const GlobeBadgeIcon = () => (
    <svg width="46" height="46" viewBox="0 0 46 46" role="img" aria-label="International">
        <circle cx="23" cy="23" r="23" fill="#1370db" />
        <g stroke="#ffffff" strokeWidth="0.9" fill="none" opacity="0.85">
            <ellipse cx="23" cy="23" rx="16" ry="16" />
            <ellipse cx="23" cy="23" rx="6.2" ry="16" />
            <ellipse cx="23" cy="23" rx="11.5" ry="16" />
            <line x1="7" y1="23" x2="39" y2="23" />
            <line x1="9.3" y1="15.5" x2="36.7" y2="15.5" />
            <line x1="9.3" y1="30.5" x2="36.7" y2="30.5" />
        </g>
    </svg>
);

/* =========================================================
   STATIC DATA
   ========================================================= */

const COUNTRY_CODES = [
    { code: "+1", name: "USA / Canada" },
    { code: "+44", name: "United Kingdom" },
    { code: "+61", name: "Australia" },
    { code: "+971", name: "UAE" },
    { code: "+65", name: "Singapore" },
    { code: "+49", name: "Germany" },
    { code: "+33", name: "France" },
    { code: "+81", name: "Japan" },
    { code: "+86", name: "China" },
    { code: "+7", name: "Russia" },
    { code: "+234", name: "Nigeria" },
    { code: "+27", name: "South Africa" },
    { code: "+94", name: "Sri Lanka" },
    { code: "+977", name: "Nepal" },
    { code: "+880", name: "Bangladesh" },
];

const FOREIGN_CODE_OPTIONS = COUNTRY_CODES.map((c) => ({
    value: c.code,
    label: `${c.code} ${c.name}`,
}));

const INDIA_CODE_OPTIONS = [{ value: "+91", label: "+91 India" }];

// Fees are stored as numbers so discounts can be calculated.
// null = not available for that delegate type.
const IEEE_DISCOUNT = 0.1; // 10% off for IEEE members

const pricingData = [
    { id: "minicourse", tier: "Mini-Course Only", intFee: 300, indFee: 5000 },
    { id: "fullaccess", tier: "Conference only", intFee: 1050, indFee: 29500 },
    { id: "retiree", tier: "Conference only for retirees", intFee: 500, indFee: 10000 },
    { id: "student", tier: "Conference only for students", intFee: 250, indFee: 5000 },
    { id: "singleday", tier: "Single-Day Entry Pass", intFee: 425, indFee: null },
];

const formatFee = (amount, isForeign) => {
    const formatted = amount.toLocaleString(isForeign ? "en-US" : "en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return isForeign ? `$ ${formatted}` : `INR ${formatted}`;
};

/* =========================================================
   VALIDATION SCHEMA
   ========================================================= */

const validationSchema = Yup.object().shape({
    delegateType: Yup.string()
        .oneOf(["indian", "foreign"], "Please select a delegate type")
        .required("Please select a delegate type"),

    aadhaarName: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) =>
            schema
                .trim()
                .min(3, "Name must be at least 3 characters")
                .max(100, "Name is too long")
                .matches(/^[a-zA-Z\u0900-\u097F\s.'-]*$/, "Name should contain valid characters only")
                .required("Name as per Aadhaar is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    email: Yup.string().trim().email("Please enter a valid email address").required("Email address is required"),

    countryCode: Yup.string().required("Country code is required"),

    mobile: Yup.string()
        .required("Mobile number is required")
        .when("delegateType", {
            is: "indian",
            then: (schema) => schema.matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number"),
            otherwise: (schema) => schema.matches(/^\d{6,15}$/, "Enter a valid mobile number (6-15 digits)"),
        }),

    whatsapp: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) => schema.nullable().matches(/^$|^[6-9]\d{9}$/, "Enter a valid 10-digit WhatsApp number"),
        otherwise: (schema) => schema.notRequired(),
    }),

    aadhaarNo: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) => schema.required("Aadhaar number is required").matches(/^\d{12}$/, "Aadhaar number must contain exactly 12 digits"),
        otherwise: (schema) => schema.notRequired(),
    }),

    passportName: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().min(3, "Name must be at least 3 characters").required("Name as per passport is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    passportNo: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().min(5, "Enter a valid passport number").max(20, "Passport number is too long").required("Passport number is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    placeOfIssue: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().min(2, "Place of issue is required").required("Place of issue is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    dateOfIssue: Yup.date().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.required("Date of issue is required").typeError("Please select a valid date"),
        otherwise: (schema) => schema.nullable().notRequired(),
    }),

    nationality: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().required("Nationality is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    affiliation: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().required("Affiliation is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    countryOfBirth: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().required("Country of birth is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
});

/* =========================================================
   REACT-SELECT THEME
   Colours are hard-coded (not CSS variables) because the menu
   is rendered in a portal on <body>, outside .pageWrapper.
   ========================================================= */

const SELECT_COLORS = {
    ink: "#14213d",
    inkSoft: "#5b6478",
    inkFaint: "#98a1b3",
    line: "#e1e5ee",
    paper: "#f7f8fb",
    surface: "#ffffff",
    brand: "#2b53a3",
    brandSoft: "#e8edf9",
    danger: "#c4442c",
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 47,
        height: 47,
        paddingLeft: 13,
        paddingRight: 4,
        borderWidth: 1.5,
        borderStyle: "solid",
        borderRadius: 11,
        borderColor: state.selectProps.hasError
            ? SELECT_COLORS.danger
            : state.isFocused
                ? SELECT_COLORS.brand
                : SELECT_COLORS.line,
        backgroundColor: state.isDisabled
            ? "#eef1f7"
            : state.isFocused
                ? SELECT_COLORS.surface
                : state.selectProps.hasError
                    ? "#fdf7f5"
                    : SELECT_COLORS.paper,
        boxShadow: state.isFocused
            ? state.selectProps.hasError
                ? "0 0 0 3px rgba(196, 68, 44, 0.1)"
                : "0 0 0 3px rgba(43, 83, 163, 0.1)"
            : "none",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        flexWrap: "nowrap",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
        "&:hover": {
            borderColor: state.isFocused ? SELECT_COLORS.brand : state.selectProps.hasError ? SELECT_COLORS.danger : "#c9d2e2",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: 0,
        marginLeft: 10,
        flexWrap: "nowrap",
        minWidth: 0,
    }),
    singleValue: (base, state) => ({
        ...base,
        margin: 0,
        fontSize: 13.5,
        color: state.isDisabled ? SELECT_COLORS.inkSoft : SELECT_COLORS.ink,
        maxWidth: "100%",
    }),
    placeholder: (base) => ({
        ...base,
        margin: 0,
        fontSize: 13.5,
        color: SELECT_COLORS.inkFaint,
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: 13.5,
        color: SELECT_COLORS.ink,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
        ...base,
        padding: "0 8px",
        color: state.isFocused ? SELECT_COLORS.brand : SELECT_COLORS.inkFaint,
        "&:hover": { color: SELECT_COLORS.brand },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
        ...base,
        marginTop: 6,
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${SELECT_COLORS.line}`,
        boxShadow: "0 14px 30px rgba(20, 33, 61, 0.14)",
    }),
    menuList: (base) => ({
        ...base,
        padding: 6,
        maxHeight: 240,
    }),
    option: (base, state) => ({
        ...base,
        borderRadius: 8,
        padding: "9px 11px",
        fontSize: 13,
        cursor: "pointer",
        color: state.isSelected ? "#ffffff" : SELECT_COLORS.ink,
        backgroundColor: state.isSelected
            ? SELECT_COLORS.brand
            : state.isFocused
                ? SELECT_COLORS.brandSoft
                : "transparent",
        "&:active": { backgroundColor: SELECT_COLORS.brandSoft },
    }),
    noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: SELECT_COLORS.inkSoft }),
};

// Puts the field icon inside the control, matching the text inputs
const SelectControl = ({ children, ...props }) => (
    <components.Control {...props}>
        <span className={styles.selectIcon}>{props.selectProps.icon}</span>
        {children}
    </components.Control>
);

/* =========================================================
   SHARED FIELD COMPONENTS
   ========================================================= */

/** Plain text / email / number input wired to Formik via render props */
const TextField = ({ label, name, type = "text", placeholder, icon, required = false, maxLength, inputMode }) => (
    <FormikField name={name}>
        {({ field, meta }) => {
            const hasError = meta.touched && meta.error;
            return (
                <div className={styles.fieldGroup}>
                    <label htmlFor={name} className={styles.fieldLabel}>
                        {label}
                        {required && <span className={styles.required}> *</span>}
                    </label>

                    <div className={`${styles.inputWrap} ${hasError ? styles.inputError : ""}`}>
                        <span className={styles.inputIcon}>{icon}</span>
                        <input
                            id={name}
                            type={type}
                            placeholder={placeholder}
                            maxLength={maxLength}
                            inputMode={inputMode}
                            className={styles.styledInput}
                            {...field}
                        />
                    </div>

                    <ErrorMessage name={name}>
                        {(msg) => <div className={styles.errorText}>{msg}</div>}
                    </ErrorMessage>
                </div>
            );
        }}
    </FormikField>
);

/** react-select dropdown wired to Formik via setFieldValue */
const SelectField = ({ label, name, options, icon, required = false, disabled = false, placeholder, isSearchable = true }) => (
    <FormikField name={name}>
        {({ field, form, meta }) => {
            const hasError = meta.touched && meta.error;
            const selected = options.find((o) => o.value === field.value) || null;

            return (
                <div className={styles.fieldGroup}>
                    <label htmlFor={name} className={styles.fieldLabel}>
                        {label}
                        {required && <span className={styles.required}> *</span>}
                    </label>

                    <Select
                        inputId={name}
                        name={name}
                        options={options}
                        value={selected}
                        onChange={(option) => form.setFieldValue(name, option ? option.value : "")}
                        onBlur={() => form.setFieldTouched(name, true)}
                        isDisabled={disabled}
                        isSearchable={isSearchable}
                        placeholder={placeholder || `Select ${label.toLowerCase()}`}
                        icon={icon}
                        hasError={!!hasError}
                        styles={selectStyles}
                        components={{ Control: SelectControl }}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        menuPlacement="auto"
                    />

                    <ErrorMessage name={name}>
                        {(msg) => <div className={styles.errorText}>{msg}</div>}
                    </ErrorMessage>
                </div>
            );
        }}
    </FormikField>
);

/** react-datepicker wired to Formik — stores a Date object in values, formats on submit */
const DatePickerField = ({ label, name, icon, required = false }) => (
    <FormikField name={name}>
        {({ field, form, meta }) => {
            const hasError = meta.touched && meta.error;

            return (
                <div className={styles.fieldGroup}>
                    <label htmlFor={name} className={styles.fieldLabel}>
                        {label}
                        {required && <span className={styles.required}> *</span>}
                    </label>

                    <div className={`${styles.inputWrap} ${hasError ? styles.inputError : ""}`}>
                        <span className={styles.inputIcon}>{icon}</span>
                        <DatePicker
                            id={name}
                            selected={field.value || null}
                            onChange={(date) => form.setFieldValue(name, date)}
                            onBlur={() => form.setFieldTouched(name, true)}
                            dateFormat="dd-MM-yyyy"
                            placeholderText="DD-MM-YYYY"
                            maxDate={new Date()}
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            autoComplete="off"
                            className={styles.styledInput}
                            wrapperClassName={styles.datePickerWrapper}
                        />
                    </div>

                    <ErrorMessage name={name}>
                        {(msg) => <div className={styles.errorText}>{msg}</div>}
                    </ErrorMessage>
                </div>
            );
        }}
    </FormikField>
);

/* =========================================================
   PAGE
   ========================================================= */

const initialValues = {
    delegateType: "",
    aadhaarName: "",
    aadhaarNo: "",
    email: "",
    countryCode: "+91",
    mobile: "",
    whatsapp: "",
    passportName: "",
    passportNo: "",
    placeOfIssue: "",
    dateOfIssue: null,   // Date object (null until user picks)
    nationality: "",
    affiliation: "",
    countryOfBirth: "",
};

const RegistrationPage = () => {
    const navigate = useNavigate();

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const displayName = values.delegateType === "foreign" ? values.passportName : values.aadhaarName;

            // Format the Date object to yyyy-MM-dd string for the backend
            const payload = {
                ...values,
                dateOfIssue: values.dateOfIssue
                    ? format(values.dateOfIssue, "yyyy-MM-dd")
                    : "",
            };

            await newRegister(payload);

            // Store delegate type so the Payment page can show correct fees.
            localStorage.setItem("ivec_delegate_type", values.delegateType);

            await Swal.fire({
                icon: "success",
                title: "Registration Successful!",
                html: `
                    <div style="line-height:1.8;text-align:left">
                        <p style="margin-bottom:8px">Thank you, <strong>${displayName}</strong>.</p>
                        <p style="color:#374151;margin:0 0 12px">
                            Your registration has been confirmed. A welcome email with your
                            <strong>username and password</strong> has been sent to your email address.
                        </p>
                        <p style="color:#6b7280;font-size:13px;background:#f0f4ff;padding:12px;border-radius:8px;margin:0">
                            📧 Please check your inbox (and spam folder) for the credentials,
                            then click <strong>Login &amp; Pay</strong> below.
                        </p>
                    </div>
                `,
                confirmButtonText: "Login & Pay →",
                confirmButtonColor: "#2b53a3",
            });

            resetForm();
            navigate("/login");
        } catch (error) {
            const serverMsg =
                error?.response?.data?.message ||
                "Unable to submit registration details. Please try again.";

            await Swal.fire({
                icon: "error",
                title: "Something went wrong",
                text: serverMsg,
                confirmButtonColor: "#3835cc",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {(formik) => {
                const isIndian = formik.values.delegateType === "indian";
                const isForeign = formik.values.delegateType === "foreign";
                const mobileMaxLength = isIndian ? 10 : 15;
                const countryCodeOptions = isIndian ? INDIA_CODE_OPTIONS : FOREIGN_CODE_OPTIONS;

                const handleDelegateChange = (type) => {
                    formik.setValues({
                        ...initialValues,
                        delegateType: type,
                        countryCode: type === "indian" ? "+91" : "+1",
                    });
                    formik.setTouched({});
                };

                return (
                    <div className={styles.pageWrapper}>
                        <div className={styles.backgroundGlowOne}></div>
                        <div className={styles.backgroundGlowTwo}></div>

                        <div className="container py-2 py-lg-3">
                            {/* ================= HEADER ================= */}
                            <div className={styles.pageHeader}>
                                <div className={styles.headerBadge}>
                                    <FaLandmark />
                                    Registration is open
                                </div>
                                <div className={styles.loginBanner}>
                                    <div className={styles.loginBannerText}>
                                        <FaCreditCard className={styles.loginBannerIcon} />
                                        <span>Already registered? Sign in to complete payment or check your payment status.</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/login")}
                                        className={styles.loginBannerBtn}
                                        id="registration-login-btn"
                                    >
                                        <FaSignInAlt /> Sign In &amp; Pay
                                    </button>
                                </div>
                            </div>

                            {/* ================= DELEGATE TYPE ================= */}
                            <div className={styles.delegateSection}>
                                <div className={styles.sectionHeading}>
                                    <div className={styles.sectionIcon}>
                                        <FaGlobeAmericas />
                                    </div>
                                    <div>
                                        <h3>Where are you joining from?</h3>
                                        <p>This decides which details we'll ask for next</p>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <button
                                            type="button"
                                            className={`${styles.delegateCard} ${isIndian ? styles.delegateActive : ""}`}
                                            onClick={() => handleDelegateChange("indian")}
                                        >
                                            <div className={styles.delegateFlag}>
                                                <IndiaFlagIcon />
                                            </div>
                                            <div className={styles.delegateContent}>
                                                <span className={styles.delegateTitle}>Indian delegate</span>
                                                <span className={styles.delegateDescription}>Registration with Aadhaar details</span>
                                            </div>
                                            {isIndian && (
                                                <div className={styles.selectedIcon}>
                                                    <FaCheckCircle />
                                                </div>
                                            )}
                                            <FaArrowRight className={styles.delegateArrow} />
                                        </button>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <button
                                            type="button"
                                            className={`${styles.delegateCard} ${isForeign ? styles.delegateActiveForeign : ""}`}
                                            onClick={() => handleDelegateChange("foreign")}
                                        >
                                            <div className={styles.delegateFlag}>
                                                <GlobeBadgeIcon />
                                            </div>
                                            <div className={styles.delegateContent}>
                                                <span className={styles.delegateTitle}>Foreign delegate</span>
                                                <span className={styles.delegateDescription}>Registration with passport details</span>
                                            </div>
                                            {isForeign && (
                                                <div className={styles.selectedIcon}>
                                                    <FaCheckCircle />
                                                </div>
                                            )}
                                            <FaArrowRight className={styles.delegateArrow} />
                                        </button>
                                    </div>
                                </div>

                                {formik.touched.delegateType && formik.errors.delegateType && (
                                    <div className={styles.errorText}>{formik.errors.delegateType}</div>
                                )}
                            </div>

                            <div className="row g-4">
                                {/* ================= FORM ================= */}
                                <div className="col-12 col-xl-8">
                                    <div className={styles.formCard}>
                                        <div className={styles.cardHeader}>
                                            <div className={styles.cardHeaderIcon}>
                                                <FaUser />
                                            </div>
                                            <div>
                                                <h2>{isForeign ? "Passport & personal details" : "Personal & contact details"}</h2>
                                                <p>Enter these exactly as they appear on your official documents.</p>
                                            </div>
                                        </div>

                                        <Form className="text-start" noValidate>
                                            {/* ================= BASIC INFO ================= */}
                                            <div className={styles.formSection}>
                                                <div className={styles.formSectionTitle}>
                                                    <span>1</span>
                                                    Basic information
                                                </div>

                                                <div className="row g-3">
                                                    {isForeign ? (
                                                        <div className="col-12">
                                                            <TextField
                                                                label="Name as per passport"
                                                                name="passportName"
                                                                placeholder="Enter name exactly as shown in passport"
                                                                icon={<FaPassport />}
                                                                required
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Name as per Aadhaar"
                                                                name="aadhaarName"
                                                                placeholder="Enter name as on Aadhaar"
                                                                icon={<FaIdCard />}
                                                                required
                                                            />
                                                        </div>
                                                    )}

                                                    {isIndian && (
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Aadhaar number"
                                                                name="aadhaarNo"
                                                                placeholder="12-digit Aadhaar number"
                                                                icon={<FaIdCard />}
                                                                required
                                                                maxLength={12}
                                                                inputMode="numeric"
                                                            />
                                                        </div>
                                                    )}

                                                    <div className="col-12 col-md-6">
                                                        <TextField
                                                            label="Email address"
                                                            name="email"
                                                            type="email"
                                                            placeholder="name@example.com"
                                                            icon={<FaEnvelope />}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ================= CONTACT ================= */}
                                            <div className={styles.formSection}>
                                                <div className={styles.formSectionTitle}>
                                                    <span>2</span>
                                                    Contact information
                                                </div>

                                                <div className="row g-3">
                                                    <div className="col-12 col-sm-5 col-md-4">
                                                        <SelectField
                                                            label="Country code"
                                                            name="countryCode"
                                                            options={countryCodeOptions}
                                                            icon={<FaGlobeAmericas />}
                                                            disabled={isIndian}
                                                            required
                                                            placeholder="Select code"
                                                        />
                                                    </div>

                                                    <div className="col-12 col-sm-7 col-md-8">
                                                        <TextField
                                                            label="Mobile number"
                                                            name="mobile"
                                                            placeholder={isIndian ? "10-digit mobile number" : "Enter mobile number"}
                                                            icon={<FaPhone />}
                                                            required
                                                            inputMode="numeric"
                                                            maxLength={mobileMaxLength}
                                                        />
                                                    </div>

                                                    {isIndian && (
                                                        <div className="col-12">
                                                            <TextField
                                                                label="WhatsApp number"
                                                                name="whatsapp"
                                                                placeholder="Enter WhatsApp number if available"
                                                                icon={<FaWhatsapp />}
                                                                maxLength={10}
                                                                inputMode="numeric"
                                                            />
                                                            <div className={styles.helperText}>WhatsApp number is optional</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* ================= FOREIGN DETAILS ================= */}
                                            {isForeign && (
                                                <div className={styles.formSection}>
                                                    <div className={styles.formSectionTitle}>
                                                        <span>3</span>
                                                        Passport information
                                                    </div>

                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Passport number"
                                                                name="passportNo"
                                                                placeholder="Enter passport number"
                                                                icon={<FaPassport />}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Place of issue"
                                                                name="placeOfIssue"
                                                                placeholder="City / country"
                                                                icon={<FaMapMarkerAlt />}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <DatePickerField
                                                                label="Date of issue"
                                                                name="dateOfIssue"
                                                                icon={<FaCalendarAlt />}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Nationality"
                                                                name="nationality"
                                                                placeholder="Enter nationality"
                                                                icon={<FaFlag />}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Affiliation"
                                                                name="affiliation"
                                                                placeholder="Organization / institution"
                                                                icon={<FaBuilding />}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <TextField
                                                                label="Country of birth"
                                                                name="countryOfBirth"
                                                                placeholder="Enter country of birth"
                                                                icon={<FaGlobeAmericas />}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* ================= SUBMIT ================= */}
                                            <div className={styles.submitArea}>
                                                <div className={styles.securityNote}>
                                                    <FaShieldAlt />
                                                    <span>Your information is handled securely and used for registration purposes only.</span>
                                                </div>

                                                <button
                                                    type="submit"
                                                    className={styles.submitBtn}
                                                    disabled={formik.isSubmitting || !formik.values.delegateType}
                                                >
                                                    <span>{formik.isSubmitting ? "Submitting..." : "Continue registration"}</span>
                                                    {!formik.isSubmitting && <FaArrowRight />}
                                                </button>

                                                <div className={styles.alreadyRegisteredCard}>
                                                    <div className={styles.alreadyRegisteredLeft}>
                                                        <FaCreditCard className={styles.cardPayIcon} />
                                                        <div>
                                                            <div className={styles.alreadyRegisteredTitle}>Already registered?</div>
                                                            <div className={styles.alreadyRegisteredSub}>Sign in with your email credentials to complete payment and view status</div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate("/login")}
                                                        className={styles.alreadyLoginBtn}
                                                        id="submit-area-login-btn"
                                                    >
                                                        <FaSignInAlt /> Sign In to Pay
                                                    </button>
                                                </div>
                                            </div>
                                        </Form>
                                    </div>
                                </div>

                                {/* ================= PRICING ================= */}
                                <div className="col-12 col-xl-4">
                                    <div className={styles.pricingCard}>
                                        <div className={styles.pricingHeader}>
                                            <div className={styles.priceIcon}>₹</div>
                                            <div>
                                                <h2>Registration fees</h2>
                                                <p>Choose the package that suits your participation.</p>
                                            </div>
                                        </div>

                                        <div className={styles.priceLegend}>
                                            <div className={isIndian ? styles.priceTabIndianActive : styles.priceTabIndian}>
                                                <FaFlag /> Indian
                                            </div>
                                            <div className={isForeign ? styles.priceTabActive : styles.priceTab}>
                                                <FaGlobeAmericas /> Foreign
                                            </div>
                                        </div>

                                        <div className={styles.pricingList}>
                                            {pricingData.map((item) => {
                                                const baseFee = isForeign ? item.intFee : item.indFee;
                                                const hasFee = baseFee !== null;
                                                const ieeeFee = hasFee ? baseFee * (1 - IEEE_DISCOUNT) : null;

                                                return (
                                                    <div key={item.id} className={styles.priceItem}>
                                                        <div className={styles.priceItemHead}>
                                                            <div className={styles.priceName}>{item.tier}</div>
                                                        </div>

                                                        {hasFee ? (
                                                            <div className={styles.priceRows}>
                                                                <div className={styles.priceRow}>
                                                                    <span className={styles.priceRowLabel}>Regular</span>
                                                                    <span className={styles.priceRowValue}>{formatFee(baseFee, isForeign)}</span>
                                                                </div>

                                                                <div className={`${styles.priceRow} ${styles.priceRowIeee}`}>
                                                                    <span className={styles.priceRowLabel}>
                                                                        IEEE member
                                                                        <span className={styles.priceBadge}>{IEEE_DISCOUNT * 100}% off</span>
                                                                    </span>
                                                                    <span className={`${styles.priceRowValue} ${styles.priceRowValueIeee}`}>
                                                                        {formatFee(ieeeFee, isForeign)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={styles.priceNA}>Not available for {isForeign ? "international" : "Indian"} delegates</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className={styles.priceNote}>
                                            <FaInfoCircle />
                                            <span>
                                                <strong>Note:</strong> Any delegate interested in attending the mini-course and the conference
                                                needs to select both options during registration and make the payment accordingly.
                                            </span>
                                        </div>

                                        <div className={styles.priceFooter}>
                                            <FaShieldAlt />
                                            <span>
                                                Registration fees are displayed in <strong>{isForeign ? "USD" : "INR"}</strong>.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ================= FOOTER NOTE ================= */}
                            <div className={styles.bottomNote}>
                                <FaShieldAlt />
                                <span>Please ensure that the information entered matches your official identification documents.</span>
                            </div>
                        </div>
                    </div>
                );
            }}
        </Formik>
    );
};

export default RegistrationPage;